import {
  HTMLAttributes,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { Stack, styled, Typography } from '@mui/material';
import clsx from 'clsx';
import { Virtuoso } from 'react-virtuoso';

import { Checkbox, useDropdownWithAutocomplete } from '../../controls';
import { ListItem, ListItemProps, ListItemText } from '../../lists';
import { VirtuosoScroller } from '../../scroller';
import { tokens } from '../../../theme/generated/tokens';
import { Criterion } from '../types';

type OwnerState = {
  allowMultiple?: boolean;
};

const Root = styled('div', {
  name: 'DsSearch',
  slot: 'Options',
  target: 'DsSearch-Options',
  overridesResolver: (_, styles) => {
    return [styles.options];
  },
})<HTMLAttributes<HTMLDivElement> & { ownerState: OwnerState }>``;

const StyledListItem = styled(ListItem, {
  name: 'DsSearch',
  slot: 'OptionsListItem',
  target: 'DsSearch-OptionsListItem',
  overridesResolver: (_, styles) => {
    return [styles.optionsListItem];
  },
})<ListItemProps & { ownerState: OwnerState }>``;

type SelectAllListItemProps<OptionItem> = {
  options: OptionItem[];
  value: string[];
  clearValue: () => void;
  selectAll: () => void;
};

const SelectAllListItem = <OptionItem,>({
  options,
  value,
  clearValue,
  selectAll,
}: SelectAllListItemProps<OptionItem>) => {
  const selectedCount = Array.isArray(value) ? value.length : 0;
  const total = options.length;
  const allChecked = total === selectedCount;
  const indeterminate = selectedCount > 0 && selectedCount < total;
  const isSelected = allChecked || indeterminate;

  const classes = clsx(
    isSelected && 'DS-Option-selected',
    'DS-Option-checkable'
  );

  const handleSelectAll = () => {
    if (isSelected) {
      clearValue();
    } else {
      selectAll();
    }
  };

  return (
    <StyledListItem
      className={classes}
      selected={isSelected}
      onClick={handleSelectAll}
      ownerState={{ allowMultiple: true }}
      startAdornment={
        <Checkbox checked={isSelected} indeterminate={indeterminate} />
      }
    >
      <ListItemText primary="Select all" />
    </StyledListItem>
  );
};

export type OptionsListProps<
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = {
  options: OptionItem[];
  renderOption?: (
    option: OptionItem,
    index: number
  ) => string | number | ReactNode;
  getOptionProps: ReturnType<
    typeof useDropdownWithAutocomplete<OptionItem, OptionValue, SelectionType>
  >['getters']['getOptionProps'];
  activeCriterion?: Criterion; // SearchBoxCriterion
  selectedOptions: Record<string, boolean>;
  toggleOption: (key: string) => void;
  listHeight: number;
  showOptions: boolean;
};

export const Options = <
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>({
  options,
  getOptionProps,
  activeCriterion,
  selectedOptions,
  toggleOption,
  listHeight,
  showOptions: _showOptions,
}: OptionsListProps<OptionItem, OptionValue, SelectionType>) => {
  const allowMultiple = activeCriterion?.allowMultiple;
  const renderOption = activeCriterion?.renderOption;
  const title = activeCriterion?.title || activeCriterion?.key;

  const [height, setHeight] = useState<number>(listHeight);
  const handleListHeight = useCallback((h: number) => {
    setHeight(h);
  }, []);

  const data = useMemo(
    () => (allowMultiple ? ['select-all', ...options] : options),
    [allowMultiple, options]
  );
  const showOptions = _showOptions && !!options.length;

  return (
    <Root data-testid="search-options-list" ownerState={{ allowMultiple }}>
      <Virtuoso
        totalListHeightChanged={handleListHeight}
        data={data}
        style={{
          // We have to give 1px height, otherwise Virtuoso will not be rendered
          height: showOptions ? height || listHeight : 1,
          maxHeight: listHeight,
        }}
        components={{
          Scroller: VirtuosoScroller,
        }}
        itemContent={(index, option) => {
          if (typeof option === 'string' && option === 'select-all') {
            return (
              <SelectAllListItem<OptionItem>
                options={options}
                value={Object.keys(selectedOptions).filter(
                  (key) => selectedOptions[key]
                )}
                clearValue={() => {
                  Object.keys(selectedOptions)
                    .filter((key) => selectedOptions[key])
                    .forEach((key) => {
                      toggleOption(key);
                    });
                }}
                selectAll={() => {
                  options.forEach((options, i) => {
                    const { key } = getOptionProps({
                      option: options,
                      index: i,
                    });
                    toggleOption(key);
                  });
                }}
              />
            );
          }
          const { onClick, ...optionProps } = getOptionProps({
            option: option as OptionItem,
            index,
          });
          const optionKey = optionProps.key;
          return (
            <StyledListItem
              key={
                optionKey +
                `${selectedOptions[optionKey] ? 'selected' : undefined}`
              }
              component="li"
              testId={`search-option-${index}`}
              onMouseDown={(e) => {
                // Prevent the dropdown from closing (input blur is discarded)
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                // Prevent the dropdown from closing (input blur is discarded)
                e.stopPropagation();
                e.preventDefault();

                if (allowMultiple) {
                  toggleOption(optionKey);
                } else {
                  onClick(e);
                }
              }}
              ownerState={{ allowMultiple }}
            >
              {allowMultiple && (
                <Checkbox checked={selectedOptions[optionKey]} />
              )}
              {renderOption ? (
                renderOption(option, index)
              ) : (
                <Stack direction="row" flexWrap="wrap">
                  {!allowMultiple && (
                    <Typography
                      component="span"
                      pr="10px"
                      sx={{ color: tokens.theme.fg.structural.primary }}
                      variant="P14M"
                      noWrap
                    >
                      {title}
                    </Typography>
                  )}
                  <Typography
                    variant="P14R"
                    sx={{ color: tokens.theme.fg.structural.primary }}
                    noWrap
                  >
                    {option as string}
                  </Typography>
                </Stack>
              )}
            </StyledListItem>
          );
        }}
        overscan={listHeight}
      />
    </Root>
  );
};
